class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

// Orphan charms cleanup: ensure charms referencing a product (_ref, _customized_at) are removed if their base product is missing
(() => {
  let cleaning = false;
  // Keep previous snapshot to detect cluster member decreases (for cascade removals)
  let prevClusterMembers = new Map(); // key => Set<line_item_key>
  let prevEngravingByCluster = new Map(); // key => boolean
  let prevInitialized = false;

  const buildBaseKey = (variantId, customizedAt) => `${variantId}::${customizedAt}`;

  subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
    if (cleaning) return;
    const cartData = event?.cartData;
    if (!cartData || !Array.isArray(cartData.items)) return;

    try {
      // Collect existing base product keys (items without _ref but with _customized_at)
      const baseKeys = new Set();
      // Index dependents by (refVariantId, customizedAt) and collect engraving bases
      const dependentsByRefAndTime = new Map(); // key => Array<item>
      const engravingBases = []; // Array<item>
      // Track base items by cluster and membership of items per cluster
      const baseByCluster = new Map(); // key => base item
      const membersByCluster = new Map(); // key => Set<line_item_key>
      for (const item of cartData.items) {
        const props = item.properties || {};
        const hasRef = Object.prototype.hasOwnProperty.call(props, '_ref');
        const customizedAt = props._customized_at;
        if (!hasRef && customizedAt && item.variant_id) {
          const key = buildBaseKey(String(item.variant_id), String(customizedAt));
          baseKeys.add(key);
          baseByCluster.set(key, item);
          if (!membersByCluster.has(key)) membersByCluster.set(key, new Set());
          if (item.key) membersByCluster.get(key).add(item.key);
        }

        // Build dependents index keyed by ref + customized_at
        if (hasRef && props._ref && customizedAt) {
          const key = buildBaseKey(String(props._ref), String(customizedAt));
          if (!dependentsByRefAndTime.has(key)) dependentsByRefAndTime.set(key, []);
          dependentsByRefAndTime.get(key).push(item);
          if (!membersByCluster.has(key)) membersByCluster.set(key, new Set());
          if (item.key) membersByCluster.get(key).add(item.key);
        }

        // Track engraving bases (items that represent an engraving cluster)
        if (!hasRef && customizedAt && item.variant_id && props._customization_type === 'engraving') {
          engravingBases.push(item);
        }
      }

      // Find charms that reference a missing base product
      const updates = {};

      // Cascade rule: if cluster membership decreased since last snapshot and cluster is engraving,
      // remove all remaining members in that cluster (base + dependents) to keep cluster atomic.
      if (prevInitialized) {
        const allClusterKeys = new Set([
          ...Array.from(prevClusterMembers.keys()),
          ...Array.from(membersByCluster.keys()),
        ]);
        for (const clusterKey of allClusterKeys) {
          const prevSet = prevClusterMembers.get(clusterKey) || new Set();
          const currSet = membersByCluster.get(clusterKey) || new Set();
          const prevSize = prevSet.size;
          const currSize = currSet.size;
          if (prevSize > currSize && currSize > 0) {
            const currBase = baseByCluster.get(clusterKey);
            const isEngravingNow = !!(
              currBase &&
              currBase.properties &&
              currBase.properties._customization_type === 'engraving'
            );
            const wasEngraving = prevEngravingByCluster.get(clusterKey) === true;
            if (isEngravingNow || wasEngraving) {
              for (const key of currSet) updates[key] = 0;
            }
          }
        }
      }

      for (const item of cartData.items) {
        const props = item.properties || {};
        const refVariantId = props._ref ? String(props._ref) : null;
        const customizedAt = props._customized_at ? String(props._customized_at) : null;
        if (refVariantId && customizedAt) {
          const key = buildBaseKey(refVariantId, customizedAt);
          if (!baseKeys.has(key)) {
            // Orphan charm -> remove by key
            if (item.key) updates[item.key] = 0;
          }
        }
      }

      // Additional rule: If an engraving item X exists but NO product with _add_on_type="engraving"
      // references it via _ref (with the same _customized_at), remove X AND all items that have
      // the same (_ref, _customized_at) combination.
      for (const engravingItem of engravingBases) {
        const customizedAt = String(engravingItem.properties?._customized_at || '');
        const variantId = String(engravingItem.variant_id || '');
        if (!customizedAt || !variantId) continue;
        const clusterKey = buildBaseKey(variantId, customizedAt);
        const dependents = dependentsByRefAndTime.get(clusterKey) || [];
        const hasEngravingAddon = dependents.some((dep) => {
          const p = dep.properties || {};
          return p._add_on_type === 'engraving';
        });
        if (!hasEngravingAddon) {
          // No engraving add-on referencing this base -> remove base and all dependents in the cluster
          if (engravingItem.key) updates[engravingItem.key] = 0;
          for (const dep of dependents) {
            if (dep.key) updates[dep.key] = 0;
          }
        }
      }

      const hasOrphans = Object.keys(updates).length > 0;
      if (!hasOrphans) {
        // Update previous snapshot to current state
        prevClusterMembers = membersByCluster;
        prevEngravingByCluster = new Map();
        for (const [k, baseItem] of baseByCluster.entries()) {
          const isEngraving = !!(
            baseItem &&
            baseItem.properties &&
            baseItem.properties._customization_type === 'engraving'
          );
          prevEngravingByCluster.set(k, isEngraving);
        }
        prevInitialized = true;
        return;
      }

      cleaning = true;
      // Build FormData to request sections for a seamless UI refresh
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      // Append each update as updates[<line_item_key>]=0 so the server parses it correctly
      for (const [key, value] of Object.entries(updates)) {
        formData.append(`updates[${key}]`, String(value));
      }

      // Prefer drawer/notification sections; fallback to main cart sections
      const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      let sectionsParam = undefined;
      if (cartUI && typeof cartUI.getSectionsToRender === 'function') {
        sectionsParam = cartUI.getSectionsToRender().map((section) => section.id);
      } else {
        const cartItemsEl = document.querySelector('cart-items');
        if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function') {
          sectionsParam = cartItemsEl.getSectionsToRender().map((section) => section.section);
        }
      }

      if (sectionsParam) {
        formData.append('sections', sectionsParam);
        formData.append('sections_url', window.location.pathname);
      }

      config.body = formData;
      const response = await fetch(`${routes.cart_update_url}`, config);
      let nextCartData;
      try {
        nextCartData = await response.json();
      } catch (_) {
        nextCartData = undefined;
      }

      if (cartUI) {
        cartUI.classList.toggle('is-empty', !nextCartData || nextCartData.item_count === 0);
      }

      // Notify UI to refresh itself
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'orphan-cleanup', cartData: nextCartData });

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) cart.renderContents(nextCartData);

      // Refresh previous snapshot from the new state after cleanup
      if (nextCartData && Array.isArray(nextCartData.items)) {
        const nextMembers = new Map();
        const nextBaseByCluster = new Map();
        for (const item of nextCartData.items) {
          const props = item.properties || {};
          const hasRef = Object.prototype.hasOwnProperty.call(props, '_ref');
          const customizedAt = props._customized_at;
          if (!customizedAt) continue;
          if (hasRef && props._ref) {
            const key = buildBaseKey(String(props._ref), String(customizedAt));
            if (!nextMembers.has(key)) nextMembers.set(key, new Set());
            if (item.key) nextMembers.get(key).add(item.key);
          } else if (item.variant_id) {
            const key = buildBaseKey(String(item.variant_id), String(customizedAt));
            if (!nextMembers.has(key)) nextMembers.set(key, new Set());
            if (item.key) nextMembers.get(key).add(item.key);
            nextBaseByCluster.set(key, item);
          }
        }
        prevClusterMembers = nextMembers;
        prevEngravingByCluster = new Map();
        for (const [k, baseItem] of nextBaseByCluster.entries()) {
          const isEngraving = !!(
            baseItem &&
            baseItem.properties &&
            baseItem.properties._customization_type === 'engraving'
          );
          prevEngravingByCluster.set(k, isEngraving);
        }
        prevInitialized = true;
      }
    } catch (e) {
      console.error('Orphan charms cleanup failed', e);
    } finally {
      cleaning = false;
    }
  });
})();

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
